import React, { useState } from 'react';
import { marked } from 'marked';
import API_Root from '../src/Constants/API_Root';
import { Pencil, Download, Check, X } from "lucide-react";
import { authFetch } from './Authentication/authService';

const extractFilename = (source) => {
  if (!source) return "";
  if (source.startsWith('s3://')) {
    const parts = source.split('/');
    return parts[parts.length - 1]; // Return the last part of the path
  }
  return source; // If not an S3 path, return the source as is
};

const aggregateUniqueFiles = (fileNames) => {
  const uniqueFiles = new Map();

  if (Array.isArray(fileNames)) {
    fileNames.forEach((file) => {
      const source = file && file.source ? file.source : '';
      const cleanedSource = extractFilename(source);

      // Use file.content if provided, otherwise fall back to filename
      const fileContent = file && file.content ? file.content : cleanedSource;

      const existingFile = uniqueFiles.get(cleanedSource);

      if (existingFile) {
        if (!existingFile.contents.includes(fileContent)) {
          existingFile.contents.push(fileContent);
        }
      } else {
        uniqueFiles.set(cleanedSource, {
          source: cleanedSource,
          contents: [fileContent],
          originalSource: source,
        });
      }
    });
  }

  return Array.from(uniqueFiles.values());
};

const FeedbackIcons = ({ onSendFeedback, conversationId, prompt, answer, onRefreshChat, handleCopyResponse }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [lastFeedbackType, setLastFeedbackType] = useState(null);
  const [comment, setComment] = useState("");

  const toggleModal = (type) => {
    if (modalVisible && type === lastFeedbackType) {
      setModalVisible(false);
    } else {
      setModalVisible(true);
      setLastFeedbackType(type);
    }
  };

  const handleRefresh = () => {
    onRefreshChat(prompt, conversationId);
  };

  const handleCopy = () => {
    handleCopyResponse(answer, conversationId);
  };

  const sendFeedback = () => {
    onSendFeedback(conversationId, lastFeedbackType, comment);
    setModalVisible(false);
    setComment("");
  };

  return (
    <div style={{ position: 'relative' }}>
      {modalVisible && (
        <div className="feedback-modal-container">
          <div className="feedback-modal">
            <textarea
              placeholder="Add any comments here..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <button onClick={sendFeedback}>Send Feedback</button>
          </div>
        </div>
      )}
      <div className="feedback-icon-container">
        <div className="feedback-icon-flex">
          <button className="copy-icon" onClick={handleCopy}></button>
          <button className="refresh-icon" onClick={handleRefresh}></button>
          <button className="thumbs-up" onClick={() => toggleModal('positive')}></button>
          <button className="thumbs-down" onClick={() => toggleModal('negative')}></button>
        </div>
      </div>
    </div>
  );
};

const SourcesList = ({ files }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!files || files.length === 0) return null;

  const displayedFiles = isExpanded ? files : files.slice(0, 3);

  const getFileName = (path) => {
    if (!path) return "";
    return path.split("/").pop();
  };

  const handleFileClick = async (e, file) => {
    e.preventDefault();

    if (!file?.source) return;

    // If already a public URL → open directly
    if (
      file.source.startsWith("http://") ||
      file.source.startsWith("https://")
    ) {
      window.open(file.source, "_blank");
      return;
    }

    // Otherwise assume S3 key
    try {
      const response = await authFetch(
        API_Root + "files/generate-presigned-urls",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ objectKey: file.source }),
        }
      );

      const data = await response.json();

      if (data?.url) {
        window.open(data.url, "_blank");
      } else {
        console.error("No presigned URL returned");
      }
    } catch (error) {
      console.error("Error generating presigned URL:", error);
    }
  };

  return (
    <div
      className="chat-primaryfile mt-2"
      style={{
        background: "#f8f9fa",
        padding: "10px",
        borderRadius: "6px",
        fontSize: "13px",
      }}
    >
      <strong>Sources:</strong>

      <ul style={{ paddingLeft: "18px", marginBottom: "6px" }}>
        {displayedFiles.map((file, index) => (
          <li
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "4px",
            }}
          >
            <a
              href="#"
              onClick={(e) => handleFileClick(e, file)}
              style={{
                textDecoration: "underline",
                cursor: "pointer",
                color: "#0d6efd",
                wordBreak: "break-all",
              }}
            >
              {getFileName(file.source)}
            </a>
          </li>
        ))}
      </ul>

      {files.length > 3 && (
        <button
          className="btn btn-sm btn-link p-0"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "Show less" : "Show more sources"}
        </button>
      )}
    </div>
  );
};

const QuestionAnswer = ({
  chatHistory,
  canvasMode,
  onSendFeedback,
  onRefreshChat,
  handleCopyResponse,
}) => {
  const [toggleState, setToggleState] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedText, setEditedText] = useState("");

  const cleanText = (text) => {
    if (typeof text !== "string") return "";
    return text.replace(/【.*?】/g, "");
  };

  const parseMarkdown = (markdownText) => {
    return marked(markdownText || "");
  };

  const toggleAnswer = (index) => {
    setToggleState((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleDownload = (content, index) => {
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `canvas-answer-${index + 1}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSaveEdit = (index) => {
    chatHistory[index].answer = editedText;
    setEditingIndex(null);
  };

  return (
    <>
      {chatHistory.map((chat, index) => {
        const cleanedAnswer = cleanText(
          toggleState[index] ? chat.answer_2 : chat.answer
        );

        const cleanedPrompt = cleanText(chat.prompt);

        const answerHtml = parseMarkdown(cleanedAnswer);
        const promptHtml = parseMarkdown(cleanedPrompt);

        /* =========================
           🟢 CANVAS MODE
        ========================== */
        if (canvasMode === "canvas") {
          return (
            <div
              key={index}
              className="p-4 mb-4 border rounded bg-white shadow-sm"
            >
              {/* Question */}
              <div className="mb-4">
                <h6 className="text-muted">Your Question</h6>
                <div
                  dangerouslySetInnerHTML={{ __html: promptHtml }}
                />
              </div>

              {/* Toolbar */}
              <div className="d-flex justify-content-end gap-2 mb-3">
                {editingIndex === index ? (
                  <>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleSaveEdit(index)}
                    >
                      Save
                    </button>

                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => setEditingIndex(null)}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setEditingIndex(index);
                        setEditedText(cleanedAnswer);
                      }}
                    >
                      Edit
                    </button>

                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() =>
                        handleDownload(cleanedAnswer, index)
                      }
                    >
                      Download
                    </button>
                  </>
                )}
              </div>

              {/* Editable / Display Area */}
              {editingIndex === index ? (
                <textarea
                  className="form-control"
                  style={{ minHeight: "400px" }}
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                />
              ) : (
                <>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: answerHtml,
                    }}
                  />

                  {/* 🔥 SOURCES IN CANVAS */}
                  {chat.file_names && chat.file_names.length > 0 && (
                    <SourcesList files={chat.file_names} />
                  )}
                </>
              )}
            </div>
          );
        }

        /* =========================
           🔵 NORMAL CHAT MODE
        ========================== */
        return (
          <React.Fragment key={index}>
            <div className="chat-user-container">
              <div className="user-chatbox">
                <div
                  className="user-text"
                  dangerouslySetInnerHTML={{ __html: promptHtml }}
                />
              </div>
              <div className="profile-user-icon" />
            </div>

            <div className="chat-assistant-container">
              <div className="profile-assistant-icon" />
              <div className="assistant-chatbox">
                <div
                  className="chat-text"
                  dangerouslySetInnerHTML={{ __html: answerHtml }}
                />

                {chat.answer_2 && (
                  <button
                    className="switch-answer-button"
                    onClick={() => toggleAnswer(index)}
                  >
                    {toggleState[index] ? "< 2/2" : "1/2 >"}
                  </button>
                )}

                {/* 🔥 SOURCES IN NORMAL MODE */}
                {chat.file_names && chat.file_names.length > 0 && (
                  <SourcesList files={chat.file_names} />
                )}
              </div>
            </div>

            <FeedbackIcons
              conversationId={chat.conversation_id}
              onSendFeedback={onSendFeedback}
              prompt={chat.prompt}
              answer={cleanedAnswer}
              onRefreshChat={onRefreshChat}
              handleCopyResponse={handleCopyResponse}
            />
          </React.Fragment>
        );
      })}
    </>
  );
};

export default QuestionAnswer;
